import { useEffect, useState, useCallback, useRef } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Search, Plus, X, Package, GripVertical, Save } from "lucide-react";
import { toast } from "sonner";

const MAX = 20;

function ProductSearch({ onPick, disabled }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/products", { params: { q: q.trim(), limit: 12 } })
        .then((r) => { setResults(r.data); setOpen(true); }).catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={boxRef} className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
      <Input data-testid="homepage-product-search" value={q} disabled={disabled}
        onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
        placeholder={disabled ? `Limit of ${MAX} reached — remove one to add more` : "Search products by name or SKU…"} className="pl-9" />
      {open && results.length > 0 && !disabled && (
        <div className="absolute left-0 right-0 top-11 z-30 rounded-xl border bg-white shadow-xl max-h-80 overflow-y-auto">
          {results.map((p) => (
            <button key={p.id} data-testid={`homepage-add-${p.sku}`}
              onClick={() => { onPick(p); setQ(""); setResults([]); setOpen(false); }}
              className="w-full flex items-center gap-3 p-2.5 text-left hover:bg-slate-50 border-b last:border-b-0">
              <span className="w-10 h-10 shrink-0 rounded-md border bg-slate-100 grid place-items-center overflow-hidden">
                {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={16} className="text-slate-300" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium leading-snug line-clamp-1">{p.name}</span>
                <span className="block text-xs text-muted-foreground font-mono">{p.sku}</span>
              </span>
              <Plus size={16} className="text-steel shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Block({ n, title, setTitle, ids, setIds, mode, setMode, catalog }) {
  const [dragIdx, setDragIdx] = useState(null);
  const items = ids.map((id) => catalog[id]).filter(Boolean);
  const auto = mode === "auto_specials";

  const add = (p) => {
    if (ids.includes(p.id)) { toast.info("Already in this block"); return; }
    if (ids.length >= MAX) { toast.error(`Maximum ${MAX} products per block`); return; }
    catalog[p.id] = { id: p.id, name: p.name, sku: p.sku, image_url: p.image_url, price: p.final_customer_price };
    setIds([...ids, p.id]);
  };
  const remove = (id) => setIds(ids.filter((x) => x !== id));
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const next = [...ids];
    const [m] = next.splice(dragIdx, 1);
    next.splice(i, 0, m);
    setIds(next); setDragIdx(null);
  };

  return (
    <div className="rounded-xl border bg-white p-6 mb-6" data-testid={`homepage-block-${n}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg">Product Block {n}</h2>
        {!auto && <span className="text-sm text-muted-foreground">{ids.length}/{MAX} selected</span>}
      </div>
      <div className="mb-4">
        <Label>Fill this block with</Label>
        <div className="mt-1.5 inline-flex rounded-lg border bg-slate-50 p-1">
          {[["manual", "Manual — pick products"], ["auto_specials", "Auto — On Special"]].map(([val, label]) => (
            <button key={val} data-testid={`homepage-mode-${n}-${val}`} onClick={() => setMode(val)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${mode === val ? "bg-steel text-white" : "text-muted-foreground hover:text-steel"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-4">
        <Label>Block heading</Label>
        <Input data-testid={`homepage-title-${n}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Featured Products" />
      </div>
      {auto ? (
        <p className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg bg-amber-50/50" data-testid={`homepage-auto-note-${n}`}>
          This block fills itself automatically with the 20 products currently on special (biggest savings first). No manual picking needed — it updates as specials change.
        </p>
      ) : (
        <>
          <div className="mb-4"><ProductSearch onPick={add} disabled={ids.length >= MAX} /></div>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg">No products yet — search above to add up to {MAX}.</p>
          ) : (
            <ul className="space-y-2">
              {items.map((p, i) => (
                <li key={p.id} data-testid={`homepage-selected-${p.sku}`} draggable
                  onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  className="flex items-center gap-3 rounded-lg border p-2 bg-slate-50">
                  <GripVertical size={16} className="text-slate-400 cursor-grab shrink-0" />
                  <span className="w-10 h-10 shrink-0 rounded-md border bg-white grid place-items-center overflow-hidden">
                    {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={16} className="text-slate-300" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-snug line-clamp-1">{p.name}</span>
                    <span className="block text-xs text-muted-foreground font-mono">{p.sku}</span>
                  </span>
                  <button data-testid={`homepage-remove-${p.sku}`} onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-500 shrink-0"><X size={18} /></button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

export default function HomepageTab() {
  const [s, setS] = useState(null);
  const catalog = useRef({}).current;

  const load = useCallback(() => {
    api.get("/admin/settings").then((r) => {
      setS(r.data);
      const seed = async () => {
        const allIds = [...(r.data.homepage_product_block1_ids || []), ...(r.data.homepage_product_block2_ids || []), ...(r.data.homepage_product_block3_ids || [])];
        for (const id of allIds) {
          if (catalog[id]) continue;
          try { const { data } = await api.get(`/products/${id}`); catalog[id] = { id, name: data.name, sku: data.sku, image_url: data.image_url, price: data.price }; setS((cur) => ({ ...cur })); } catch { /* skip */ }
        }
      };
      seed();
    });
  }, [catalog]);
  useEffect(() => { load(); }, [load]);

  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        homepage_product_block1_title: s.homepage_product_block1_title,
        homepage_product_block2_title: s.homepage_product_block2_title,
        homepage_product_block3_title: s.homepage_product_block3_title,
        homepage_product_block1_ids: (s.homepage_product_block1_ids || []).slice(0, MAX),
        homepage_product_block2_ids: (s.homepage_product_block2_ids || []).slice(0, MAX),
        homepage_product_block3_ids: (s.homepage_product_block3_ids || []).slice(0, MAX),
        homepage_product_block1_mode: s.homepage_product_block1_mode || "manual",
        homepage_product_block2_mode: s.homepage_product_block2_mode || "manual",
        homepage_product_block3_mode: s.homepage_product_block3_mode || "manual",
      });
      toast.success("Homepage product blocks saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-extrabold mb-1">Homepage Product Blocks</h1>
      <p className="text-muted-foreground mb-6">Pick up to {MAX} products for each block shown above the footer (6 visible at a time on desktop, scroll for more). Toggle each block on/off under Commerce Settings → Homepage.</p>

      <Block n={1} title={s.homepage_product_block1_title || ""} setTitle={(v) => set("homepage_product_block1_title", v)}
        ids={s.homepage_product_block1_ids || []} setIds={(v) => set("homepage_product_block1_ids", v)}
        mode={s.homepage_product_block1_mode || "manual"} setMode={(v) => set("homepage_product_block1_mode", v)} catalog={catalog} />
      <Block n={2} title={s.homepage_product_block2_title || ""} setTitle={(v) => set("homepage_product_block2_title", v)}
        ids={s.homepage_product_block2_ids || []} setIds={(v) => set("homepage_product_block2_ids", v)}
        mode={s.homepage_product_block2_mode || "manual"} setMode={(v) => set("homepage_product_block2_mode", v)} catalog={catalog} />
      <Block n={3} title={s.homepage_product_block3_title || ""} setTitle={(v) => set("homepage_product_block3_title", v)}
        ids={s.homepage_product_block3_ids || []} setIds={(v) => set("homepage_product_block3_ids", v)}
        mode={s.homepage_product_block3_mode || "manual"} setMode={(v) => set("homepage_product_block3_mode", v)} catalog={catalog} />

      <Button data-testid="homepage-save" onClick={save} className="bg-accent text-steel hover:bg-amber-600 font-semibold">
        <Save size={16} className="mr-2" /> Save Product Blocks
      </Button>
    </div>
  );
}
